import { useState } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { Input } from "../components/ui/input"
import { cn } from "../lib/utils"

export function ComboboxRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")

  const placeholder = element.placeholder
    ? String(resolveSymbol(element.placeholder) ?? "")
    : "Search..."

  const [selectedValue, setSelectedValue] = useBoundValue<string>(
    element.bind,
    resolveSymbol,
    "",
    String,
  )

  const options = element.children ?? []
  const filtered = options.filter((opt) => {
    const label = (opt.text ?? "").toLowerCase()
    return !search || label.includes(search.toLowerCase())
  })

  // Find display label for selected value
  const selectedLabel = options.find(
    (o) => (o.variant ?? o.text ?? o.id) === selectedValue,
  )?.text ?? ""

  const handleSelect = (opt: typeof options[number]) => {
    const value = opt.variant ?? opt.text ?? opt.id
    setSelectedValue(value)
    dispatchEvent(element.id, "change", { value })
    setOpen(false)
    setSearch("")
  }

  return (
    <div
      className={cn("relative", element.className)}
      data-testid={`combobox-${element.id}`}
    >
      <button
        type="button"
        data-slot="combobox-trigger"
        className="cn-input flex h-10 w-full items-center justify-between"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-label={element.ariaLabel ?? placeholder}
      >
        <span className={selectedValue ? "text-foreground" : "text-muted-foreground"}>
          {selectedValue ? selectedLabel || selectedValue : placeholder}
        </span>
        <svg
          className={cn("ml-2 h-4 w-4 shrink-0 opacity-50 transition-transform", open && "rotate-180")}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="cn-popover absolute z-50 mt-1 w-full">
          <div className="p-1">
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={placeholder}
              className="h-8"
              autoFocus
            />
          </div>
          <div className="max-h-[200px] overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">
                No results found.
              </div>
            ) : (
              filtered.map((opt) => {
                const label = opt.text ? String(resolveSymbol(opt.text) ?? "") : opt.id
                const optValue = opt.variant ?? opt.text ?? opt.id
                return (
                  <button
                    key={opt.id}
                    type="button"
                    data-slot="combobox-item"
                    className={cn(
                      "cn-select-item relative flex w-full cursor-default select-none items-center",
                      selectedValue === optValue && "bg-accent",
                    )}
                    onClick={() => handleSelect(opt)}
                  >
                    {label}
                  </button>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
